import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { toast } from 'react-toastify';
import {
  IConcreteType,
  ICreateOrUpdatePourPayload,
  IDashboardPour,
  IEnhancedPdfDetails,
  IPourDetails,
  IPourExportDetailsItem,
  IProjectDashboardDetails,
  IProjectItem,
  IProjectPourCardData,
  ITechnicalScreen,
  ITechnicalScreenDevice,
  ITechnicalScreenHub,
  IEditHub,
  IConcreteMix,
  IPSIConcreteType,
} from '@/models';
import { http, sleep } from '@/utils';
import { API_ENDPOINTS, LoadingKeys } from '@/constants';
import { handleApiCallInStore } from './utils';
import { t } from 'i18next';


export enum Tabs {
  NEW = "new",
  PROGRESSING  = "progressing",
  FINISHED = "finished",
}

export interface IProjectState {
  pwaVersion: any;
  MaintenanceMode: any;
  ClientDevice: any[];
  ClientBananas: any[];
  pourReportData: IPourExportDetailsItem[];
  pdfDetails: IEnhancedPdfDetails;
  concreteTypes: IConcreteType[];
  psiConcreteTypes: IPSIConcreteType[];
  projectPours: IProjectPourCardData[];
  projectDetails: { name: string; id: string };
  pourDetails: IPourDetails;
  projects: IProjectItem[];
  dashboardPours: IDashboardPour[];
  technicalScreenData: ITechnicalScreen[];
  concreteMix: IConcreteMix[];
  currentTabSelected:Tabs;
  getProjectDashboard: (projectId: string) => void;
  getPwaVersion: () => any;
  getMaintenanceMode: () => any;
  getClientDevices: () => any;
  getClientBananas: () => any;
  getConcreteTypes: (is_mpa: boolean) => void;
  getProjects: (onSuccess: (data: IProjectItem[]) => void) => void;
  // getProjectsDetails: (id: string) => void;
  getProjectPours: (projectId: string) => void;
  getPourGaugeDetails: (
    id: string,
    onSuccess: (data: IPourDetails) => void
  ) => void;
  currentExportPourId: number;
  markPourAsDone: (
    projectId: string,
    id: number,
    onSuccess: () => void
  ) => void;
  exportPourData: (
    pourId: number,
    pdfDetails: IEnhancedPdfDetails,
    onSuccess: (data: IPourExportDetailsItem[]) => void
  ) => void;
  createOrUpdatePour: (data: {
    payload: ICreateOrUpdatePourPayload;
    onSuccess: () => void;
    pourId?: string;
  }) => void;
  getTechnicalScreen: (projectId: number) => void;
  deleteHub: (hubId: string, onSuccess: () => void) => void;
  editHub:(
    hubId: string,
    data: IEditHub,
    onSuccess?: (data: any) => void
  ) => void;
  getConcreteMix: (projectId: number) => void;
  getCurrentTabSelected: ()=> string;
  setCurrentTabSelected: (currentTab:string) => void;
}

export const DEFAULT_SELECTED_TAB = Tabs.PROGRESSING

export const useProjectStore = create(
  persist<IProjectState>(
    (set, get) => ({
      ClientDevice: [],
      ClientBananas: [],
      pwaVersion: null,
      MaintenanceMode: undefined,
      pdfDetails: undefined,
      pourReportData: [],
      pourDetails: undefined,
      sensorData: undefined,
      projectDetails: undefined,
      projectPoursCategories: [],
      projectPours: [],
      dashboardPours: [],
      concreteTypes: [],
      psiConcreteTypes: [],
      currentExportPourId: undefined,
      projects: [],
      pourSensors: {},
      technicalScreenData: [],
      concreteMix: [],
      currentTabSelected: DEFAULT_SELECTED_TAB,
      getMaintenanceMode: () =>
        handleApiCallInStore(LoadingKeys.maintenanceMode, async () => {
          const { data } = await http.get(
          API_ENDPOINTS.project.maintenanceMode,
        );
          set(() => ({ MaintenanceMode: data}));
      }),
      getTechnicalScreen: (projectId: number) =>
        handleApiCallInStore(LoadingKeys.TechnicalScreen, async () => {
          try {
            const { data } = await http.get<{ project_devices: ITechnicalScreenDevice[]; project_hubs: ITechnicalScreenHub [] }>(
              API_ENDPOINTS.project.technicalScreenData(projectId)
            );
            // Map the data to match the ITechnicalScreen interface
            const technicalScreenData: ITechnicalScreen[] = [
              {
                project_devices: data.project_devices,
                project_hubs: data.project_hubs
              }
            ];
      
            set(() => ({ technicalScreenData }));
          } catch (error) {
            console.error("Error fetching technical screen data:", error);
          }
        }),
      deleteHub: (hubId: string, onSuccess) =>
        handleApiCallInStore(LoadingKeys.DeleteHub, async () => {
          await http.delete(API_ENDPOINTS.project.deleteHub(hubId));
          //toast.success('the Hub is deleted');
          onSuccess();
        }),
      editHub(hubId: string, data: any, onSuccess?: (data: any) => void) {
         handleApiCallInStore(LoadingKeys.EditHub, async () => {
          await http.put<IEditHub>(API_ENDPOINTS.project.editHub(hubId), data);
          
          if (onSuccess) {
              onSuccess(data); 
          }
        });
      },
      getPwaVersion: () =>
        handleApiCallInStore(LoadingKeys.pwaVersion, async () => {
          const { data } = await http.get<{ version: string }>(
            API_ENDPOINTS.project.pwaVersion
          );
          set(() => ({ pwaVersion: data.version }));
        }),
      getConcreteTypes: (is_mpa) =>
      {
        if(is_mpa){
          handleApiCallInStore(LoadingKeys.ConcreteTypes, async () => {
            const { data } = await http.get<{ concreteTypes: IConcreteType[] }>(
              API_ENDPOINTS.project.concreteTypes
            );
            set(() => ({ concreteTypes: data?.concreteTypes }));
            set(() => ({ psiConcreteTypes: [] }));
          })
        }else{
          handleApiCallInStore(LoadingKeys.psiConcreteTypes, async () => {
            const { data } = await http.get<{ concreteTypes: IPSIConcreteType[] }>(
              API_ENDPOINTS.project.psiConcreteTypes
            );
            set(() => ({ psiConcreteTypes: data?.concreteTypes }));
            set(() => ({concreteTypes:[]}))
          })
        }

      },

      getConcreteMix: (projectId: number) =>
        handleApiCallInStore(LoadingKeys.ConcreteMix, async () => {
          try {
            const { data } = await http.get<IConcreteMix[]>(
              API_ENDPOINTS.project.getConcreteMix(projectId)
            ); // Change the expected type here to IConcreteMix[]
      
            if (data && data.length > 0) {
              set((state) => ({
                ...state,
                concreteMix: data, // Update with the array of concrete mixes directly
                concreteMixError: "",
              }));
            } else {
              console.warn("No concrete mix data found for project:", projectId);
              set((state) => ({
                ...state,
                concreteMixError: "No concrete mix data found.",
              }));
            }
          } catch (error) {
            console.error("Error fetching concrete mix:", error);
            toast.error("Failed to fetch concrete mix. Please try again later.");
            set((state) => ({
              ...state,
              concreteMixError: "Failed to fetch concrete mix.",
            }));
          }
        }),   
      getProjects: onSuccess =>
        handleApiCallInStore(LoadingKeys.Projects, async () => {
          const { data } = await http.get<{ projects: IProjectItem[] }>(
            API_ENDPOINTS.project.projectspwa
          );
          set(() => ({ projects: data?.projects }));
          onSuccess(data?.projects);
        }),
      getProjectDashboard: projectId =>
        handleApiCallInStore(LoadingKeys.ProjectDashboard, async () => {
          const { data } = await http.get<IProjectDashboardDetails>(
            API_ENDPOINTS.project.projectPours(projectId)
          );
          set(() => ({ dashboardPours: data.projectInfo.all_proj_pours }));
        }),
      getClientDevices: () =>
        handleApiCallInStore(LoadingKeys.ClientSensor, async () => {
          const { data } = await http.get(
            API_ENDPOINTS.project.ClientSensor
          );
          set(() => ({ ClientDevice: data }));
      }),
      getClientBananas: () =>
        handleApiCallInStore(LoadingKeys.ClientBananas, async () => {
          const { data } = await http.get(
            API_ENDPOINTS.project.ClientBananas
          );
          set(() => ({ ClientBananas: data }));
      }),
      getProjectPours: projectId =>
        handleApiCallInStore(LoadingKeys.ProjectDashboard, async () => {
          const { data } = await http.get<IProjectDashboardDetails>(
            API_ENDPOINTS.project.projectPours(projectId)
          );
          const allPoursMap: Map<string | number, IProjectPourCardData> =
            new Map();
          set(() => ({
            projectDetails: {
              name: data?.projectInfo?.all_proj_pours?.[0]?.proj_name,
              id: projectId,
            },
          }));
          data.projectInfo.all_proj_pours
            .sort(
              (a, b) =>
                new Date(a.min_cast_start_time_utc).valueOf() -
                new Date(b.min_cast_start_time_utc).valueOf()
            )
            .forEach(pourItem => {
              const errors = [];
              data.projectInfo.all_proj_alert.forEach(alertItem => {
                if (alertItem.error && alertItem.pour_id === pourItem.id) {
                  errors.push(alertItem);
                }
              });
              allPoursMap.set(pourItem.id, {
                pour_id: pourItem.id,
                status: !pourItem.active?'finished':
                pourItem.min_cast_start_time_utc == null? 'new' : 'progressing',
                pour_name: pourItem.name,
                pour_start_time: pourItem.min_cast_start_time_utc,
                highest_known_strengh: Number(pourItem.highest_known_strengh),
                b_type_name: pourItem.b_type_name,
                errors,
                last_humidity: pourItem.last_humidity?.toFixed(0),
                use_humidity: pourItem.use_humidity,
                expected_finish: pourItem.active? pourItem.expected_finish : null,
                finish_at: pourItem.finish_at,
                active: pourItem.active
              });
            });
          set(() => ({
            projectPours: Array.from(allPoursMap.values()),
          }));
        }),

      markPourAsDone: (projectId, id, onSuccess) =>
        handleApiCallInStore(LoadingKeys.MarkPourAsDone, async () => {
          await http.post(API_ENDPOINTS.project.markPourAsDone(projectId, id));
          toast.success(t('thePourMarkedAsDoneSuccessfully'));
          onSuccess();
          get().getProjectPours(projectId);
        }),
      exportPourData: (pourId, pdfDetails, onSuccess) =>
        handleApiCallInStore(LoadingKeys.ExportPourDetails, async () => {
          set(() => ({
            currentExportPourId: pourId,
          }));
          const { data } = await http.get<IPourExportDetailsItem[]>(
            API_ENDPOINTS.project.pourDetails(pourId)
          );
          set(() => ({
            pourReportData: data,
            pdfDetails,
          }));
          await onSuccess(data);
        }),
      createOrUpdatePour: ({ payload, onSuccess, pourId }) =>
        handleApiCallInStore(LoadingKeys.CreateOrUpdatePour, async () => {
          if (pourId !== undefined) {
            await http.put<ICreateOrUpdatePourPayload>(
              API_ENDPOINTS.project.updatePour(pourId),
              payload
            );
          } else {
            await http.post<ICreateOrUpdatePourPayload>(
              API_ENDPOINTS.project.createPour,
              payload
            );
          }

          await onSuccess();
        }),
      getPourGaugeDetails: (id, onSuccess) =>
        handleApiCallInStore(LoadingKeys.PourGaugeDetails, async () => {
          const { data } = await http.get<
            { snapshot: IPourDetails }[] | { pour_data: IPourDetails[] }
          >(API_ENDPOINTS.project.pourGaugeDetails(id));

          let pourDetailsData = {} as IPourDetails;
          if ('pour_data' in data) {
            pourDetailsData = data?.pour_data?.[0];
          } else {
            pourDetailsData = data?.[0]?.snapshot;
            pourDetailsData.b_type =
              data?.[0]?.snapshot.concretes?.[0]?.concrete_type;
          }

          set(() => ({
            pourDetails: pourDetailsData,
          }));
          onSuccess(pourDetailsData);
        }),
        getCurrentTabSelected:()=>{
          return get().currentTabSelected
        },
        setCurrentTabSelected:(currentTab:Tabs)=>{
           set((state) => ({
              ...state,
              currentTabSelected: currentTab,
            }));
        }
    }),
    {
      name: 'Project-storage',
      getStorage: () => sessionStorage,
    }

  )
);
